import React from "react";
import { cn } from "@/lib/utils";

/**
 * Typography variant definitions.
 *
 * Naming convention:
 *   h1–h6       → headings
 *   subtitle1/2 → prominent supporting text
 *   body1/2     → default body copy (base / sm)
 *   caption     → small helper text
 *   overline    → uppercase label / category text
 *   label       → form field label
 *   error       → validation error text
 */

const variantMap = {
  // ── Headings ──
  h1: {
    tag: "h1" as const,
    classes: "text-4xl md:text-6xl font-bold text-gray-900 dark:text-gray-100",
  },
  h2: {
    tag: "h2" as const,
    classes: "text-3xl font-bold text-gray-900 dark:text-gray-100",
  },
  h3: {
    tag: "h3" as const,
    classes: "text-2xl font-bold text-gray-900 dark:text-gray-100",
  },
  h4: {
    tag: "h4" as const,
    classes: "text-xl font-semibold text-gray-900 dark:text-gray-100",
  },
  h5: {
    tag: "h5" as const,
    classes: "text-lg font-semibold text-gray-800 dark:text-gray-200",
  },
  h6: {
    tag: "h6" as const,
    classes: "text-base font-semibold text-gray-800 dark:text-gray-200",
  },

  // ── Subtitles ──
  subtitle1: {
    tag: "p" as const,
    classes: "text-xl text-gray-600 dark:text-gray-400",
  },
  subtitle2: {
    tag: "p" as const,
    classes: "text-lg text-gray-600 dark:text-gray-400",
  },

  // ── Body ──
  body1: {
    tag: "p" as const,
    classes: "text-base text-gray-600 dark:text-gray-400",
  },
  body2: {
    tag: "p" as const,
    classes: "text-sm text-gray-600 dark:text-gray-400",
  },

  // ── Small / Helper ──
  caption: {
    tag: "span" as const,
    classes: "text-xs text-gray-500 dark:text-gray-400",
  },
  overline: {
    tag: "span" as const,
    classes: "text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500",
  },
  label: {
    tag: "label" as const,
    classes: "text-sm font-medium text-gray-700 dark:text-gray-300",
  },
  error: {
    tag: "span" as const,
    classes: "text-xs text-red-400",
  },
} as const;

type Variant = keyof typeof variantMap;
type ElementTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "label" | "div" | "li" | "dt" | "dd" | "figcaption" | "blockquote" | "legend";

export interface TypographyProps {
  /** The visual style to apply */
  variant?: Variant;
  /** Override the rendered HTML element (defaults to the variant's tag) */
  as?: ElementTag;
  /** Additional class names (merged with variant classes; can override) */
  className?: string;
  /** htmlFor — forwarded when rendered as <label> */
  htmlFor?: string;
  children: React.ReactNode;
}

const Typography = ({
  variant = "body1",
  as,
  className,
  htmlFor,
  children,
}: TypographyProps) => {
  const { tag, classes } = variantMap[variant];
  const Component = (as ?? tag) as React.ElementType;

  return (
    <Component className={cn(classes, className)} {...(Component === "label" ? { htmlFor } : {})}>
      {children}
    </Component>
  );
};

export default Typography;
